import { Link, NavLink, useLocation, useNavigate } from "react-router-dom"
import { useState } from "react"
import { Menu, X, GraduationCap, ChevronDown } from "lucide-react"
import { SCHOOL, SITE_NAV } from "../../lib/constants"
import { Button } from "../ui/Button"
import { ThemeToggle } from "../ui/ThemeToggle"
import { cn } from "../../lib/utils"

export function SiteHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const isActive = (href: string) => href === "/" ? location.pathname === "/" : location.pathname.startsWith(href)

  return (
    <header className="sticky top-0 z-40 bg-white/95 dark:bg-ink-950/95 backdrop-blur border-b border-ink-200 dark:border-ink-800">
      {/* Top utility bar */}
      <div className="bg-brand-800 dark:bg-brand-950 text-brand-50 text-xs">
        <div className="container mx-auto flex items-center justify-between px-4 py-2">
          <span className="opacity-90 hidden sm:block">{SCHOOL.motto}</span>
          <div className="flex items-center gap-3 ml-auto">
            <Link to="/portal/login" className="hover:text-gold-300">Student Portal</Link>
            <span className="opacity-40 hidden sm:inline">|</span>
            <span className="hidden sm:inline opacity-90">{SCHOOL.phone}</span>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-900 p-1 ring-2 ring-gold-400/40 group-hover:ring-gold-400 transition-all overflow-hidden">
              <img src="/logo.png" alt={SCHOOL.name} className="w-full h-full object-contain rounded-full" />
            </div>
            <div className="hidden md:block">
              <div className="font-display font-bold text-brand-900 dark:text-brand-200 text-base leading-tight">
                Yusuf Maitama Sule
              </div>
              <div className="text-[11px] uppercase tracking-wider text-brand-700 dark:text-brand-400 font-medium">
                College of Education • Kano Ghari
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {SITE_NAV.map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                className={cn(
                  "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  isActive(item.href)
                    ? "text-brand-800 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/40"
                    : "text-ink-700 dark:text-ink-300 hover:text-brand-800 dark:hover:text-brand-300 hover:bg-ink-50 dark:hover:bg-ink-800/50"
                )}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/portal/login" className="hidden sm:inline-block">
              <Button size="sm" className="bg-brand-800 dark:bg-brand-700 hover:bg-brand-900 dark:hover:bg-brand-800 text-white shadow-sm">
                <GraduationCap className="w-4 h-4 mr-1.5" />
                Student Portal
              </Button>
            </Link>

            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-md text-ink-700 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden border-t border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-950">
          <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
            {SITE_NAV.map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setOpen(false)}
                className={cn(
                  "px-4 py-2.5 text-sm font-medium rounded-md transition-colors",
                  isActive(item.href)
                    ? "text-brand-800 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/40"
                    : "text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800"
                )}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="my-2 h-px bg-ink-200 dark:bg-ink-800" />
            <Link
              to="/portal/login"
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 text-sm font-semibold rounded-md bg-brand-800 dark:bg-brand-700 text-white"
            >
              Student Portal Login
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}
